/**
 * FeedItem — Linha do feed unificado.
 *
 * Spec §3.2 — cada item:
 *  - Checkbox de seleção (bulk actions)
 *  - SourceIcon + SeverityBadge
 *  - Title + message + meta (occurred_at relative)
 *  - Link "ver detalhes" → navigate(item.link)
 *  - Expanded panel inline com source_meta dump
 */
import { Group, Checkbox, Stack, Text, Badge, ActionIcon, Box } from '@mantine/core'
import { IconChevronDown, IconChevronRight, IconExternalLink } from '@tabler/icons-react'
import { useNavigate } from 'react-router-dom'
import { SourceIcon, getSourceColor } from './SourceIcon'
import type { FeedItem as FeedItemType, Severity } from '@/api/notifications-feed'

interface Props {
  item: FeedItemType
  isSelected: boolean
  onToggleSelect: (id: number) => void
  isExpanded: boolean
  onToggleExpand: (id: number) => void
}

const SEVERITY_COLORS: Record<Severity, string> = {
  debug: 'gray',
  info: 'blue',
  warning: 'yellow',
  critical: 'red',
  fatal: 'grape',
}

function timeAgo(iso: string): string {
  const d = new Date(iso).getTime()
  const now = Date.now()
  const diffSec = Math.floor((now - d) / 1000)
  if (diffSec < 60) return 'agora'
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}min`
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h`
  return `${Math.floor(diffSec / 86400)}d`
}

export function FeedItem({ item, isSelected, onToggleSelect, isExpanded, onToggleExpand }: Props) {
  const navigate = useNavigate()
  const isUnread = item.read_at == null
  const color = SEVERITY_COLORS[item.severity] ?? 'blue'
  const sourceColor = getSourceColor(item.source)

  const handleOpenLink = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (item.link) {
      if (item.link.startsWith('http')) {
        window.open(item.link, '_blank', 'noopener,noreferrer')
      } else {
        navigate(item.link)
      }
    }
  }

  return (
    <Box
      data-testid={`feed-item-${item.event_id}`}
      px="md"
      py="sm"
      style={{
        borderBottom: '1px solid var(--ai-border-light)',
        background: isUnread ? 'var(--ai-bg-subtle, transparent)' : 'transparent',
        cursor: 'pointer',
      }}
      onClick={() => onToggleExpand(item.event_id)}
    >
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <Checkbox
          checked={isSelected}
          onChange={() => onToggleSelect(item.event_id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Selecionar evento ${item.event_id}`}
        />

        <Box mt={4}><SourceIcon source={item.source} size={20} /></Box>

        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <Badge size="xs" color={color} variant={isUnread ? 'filled' : 'light'}>
              {item.severity}
            </Badge>
            <Text size="xs" c={sourceColor} tt="uppercase" fw={600}>
              {item.source}
            </Text>
            {item.coalesced_count && item.coalesced_count > 1 && (
              <Badge size="xs" variant="outline" color="gray">
                +{item.coalesced_count - 1} similares
              </Badge>
            )}
            <Text size="xs" c="dimmed">
              {timeAgo(item.occurred_at)}
            </Text>
          </Group>

          <Text size="sm" fw={isUnread ? 600 : 400} lineClamp={2}>
            {item.title}
          </Text>
          {item.message && (
            <Text size="xs" c="dimmed" lineClamp={2}>
              {item.message}
            </Text>
          )}

          {isExpanded && (
            <Box mt="xs" p="sm" style={{
              background: 'var(--ai-bg-elevated)',
              borderRadius: 4,
              fontFamily: 'monospace',
              fontSize: 11,
              overflow: 'auto',
            }}>
              <pre style={{ margin: 0 }}>
                {JSON.stringify({
                  event_type: item.event_type,
                  tenant_id: item.tenant_id,
                  occurred_at: item.occurred_at,
                  delivered_at: item.delivered_at,
                  source_meta: item.source_meta,
                }, null, 2)}
              </pre>
            </Box>
          )}
        </Stack>

        <Group gap={4} wrap="nowrap">
          {item.link && (
            <ActionIcon
              variant="subtle"
              size="sm"
              aria-label="Abrir detalhes"
              onClick={handleOpenLink}
            >
              <IconExternalLink size={16} />
            </ActionIcon>
          )}
          <ActionIcon
            variant="subtle"
            size="sm"
            aria-label={isExpanded ? 'Recolher' : 'Expandir'}
            onClick={(e) => { e.stopPropagation(); onToggleExpand(item.event_id) }}
          >
            {isExpanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Group>
      </Group>
    </Box>
  )
}
